// presence.ts — "presence, never streaks". We count distinct days the user showed
// up within a rolling window and show "You've shown up X of the last 21 days."
// Rules: no streak, no zero/broken state, no loss framing. A missed day simply
// doesn't increment; absence is met with "Welcome back", never guilt.
import { dayKey, dayKeyOffset } from './dates';

/** Rolling window for the presence line (clinician-tunable). */
export const PRESENCE_WINDOW = 21;

/** Days shown up within the last PRESENCE_WINDOW days (inclusive of today). */
export function presenceCount(days: string[], now: Date = new Date()): number {
  const cutoff = dayKeyOffset(PRESENCE_WINDOW - 1, now); // YYYY-MM-DD strings sort chronologically
  const set = new Set(days.filter((d) => d >= cutoff));
  return set.size;
}

/** Whole days since the user was last present before today (0 if first-ever or
 *  already seen today). Used by the guide's H2 return-after-away hook. */
export function daysAway(days: string[], now: Date = new Date()): number {
  const today = dayKey(now);
  const prior = days.filter((d) => d < today);
  if (!prior.length) return 0;
  const last = prior.sort()[prior.length - 1];
  const ms = new Date(today).getTime() - new Date(last).getTime();
  return Math.max(0, Math.round(ms / 86_400_000));
}

/** True if the user has been away — last presence (before today) was before
 *  yesterday — so we can say a warm "Welcome back". */
export function wasAway(days: string[], now: Date = new Date()): boolean {
  const today = dayKey(now);
  const prior = days.filter((d) => d < today);
  if (!prior.length) return false;
  const last = prior.sort()[prior.length - 1];
  return last < dayKeyOffset(1, now); // last seen before yesterday
}
